import { ReactNode, forwardRef, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  ButtonBase,
  ButtonBaseProps,
  InputBase,
  Menu,
  MenuItem,
  Tooltip,
  makeStyles,
} from '@material-ui/core';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import SearchIcon from '@material-ui/icons/Search';
import CheckIcon from '@material-ui/icons/Check';
import { MONO_FONT, fluxTokens } from './tokens';

const useStyles = makeStyles(theme => {
  const t = fluxTokens(theme);
  return {
    header: {
      flexShrink: 0,
      display: 'flex',
      alignItems: 'center',
      gap: 16,
      minHeight: 56,
      padding: '8px 20px',
      borderBottom: `1px solid ${t.border}`,
      background: t.panelBg,
      flexWrap: 'wrap',
    },
    crumbs: {
      display: 'flex',
      alignItems: 'center',
      gap: 6,
      fontSize: 12.5,
      color: t.textMuted,
      whiteSpace: 'nowrap',
      '& a': {
        color: t.textMuted,
        textDecoration: 'none',
        '&:hover': { color: t.text },
      },
    },
    crumbSep: { opacity: 0.6 },
    crumbCurrent: { color: t.accent, fontWeight: 500 },
    titleWrap: {
      display: 'flex',
      alignItems: 'center',
      gap: 10,
      minWidth: 0,
      flex: 1,
    },
    title: {
      margin: 0,
      fontSize: 16,
      fontWeight: 700,
      color: t.text,
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      letterSpacing: '-0.01em',
    },
    countPill: {
      flexShrink: 0,
      fontFamily: MONO_FONT,
      fontSize: 11,
      padding: '3px 8px',
      borderRadius: 999,
      color: t.accent,
      background: t.accentBg,
      border: `1px solid ${t.accentBorder}`,
      whiteSpace: 'nowrap',
    },
    actions: {
      display: 'flex',
      alignItems: 'center',
      gap: 8,
      marginLeft: 'auto',
    },
    btn: {
      height: 32,
      padding: '0 12px',
      borderRadius: 8,
      fontFamily: 'inherit',
      fontSize: 12.5,
      fontWeight: 600,
      gap: 6,
      whiteSpace: 'nowrap',
      textDecoration: 'none',
      transition: 'background-color 0.15s ease, border-color 0.15s ease, opacity 0.15s',
      '& svg': { fontSize: 16 },
      '&.Mui-disabled': { opacity: 0.45 },
    },
    btnPrimary: {
      color: '#fff',
      background: t.primaryGradient,
      boxShadow: '0 4px 14px rgba(124,58,237,0.28)',
      '&:hover': { filter: 'brightness(1.08)' },
    },
    btnGhost: {
      color: t.text,
      background: t.surface,
      border: `1px solid ${t.borderStrong}`,
      '&:hover': { borderColor: t.accentBorder },
    },
    btnIcon: {
      width: 32,
      padding: 0,
    },
    btnSmall: {
      height: 28,
      padding: '0 10px',
      fontSize: 12,
      borderRadius: 7,
    },
    btnActive: {
      color: t.accent,
      background: t.accentBg,
      borderColor: t.accentBorder,
    },
    select: {
      width: '100%',
      height: 32,
      padding: '0 10px',
      borderRadius: 8,
      border: `1px solid ${t.borderStrong}`,
      background: t.inputBg,
      color: t.text,
      fontFamily: 'inherit',
      fontSize: 12.5,
      justifyContent: 'space-between',
      gap: 6,
      '&:hover': { borderColor: t.accentBorder },
      '& svg': { fontSize: 16, color: t.textMuted },
    },
    selectLabel: {
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
      textAlign: 'left',
    },
    menuPaper: {
      background: t.surface,
      border: `1px solid ${t.borderStrong}`,
      borderRadius: 10,
      maxHeight: 340,
      minWidth: 200,
    },
    menuItem: {
      fontSize: 12.5,
      minHeight: 30,
      gap: 8,
      paddingTop: 4,
      paddingBottom: 4,
    },
    menuCheck: {
      width: 16,
      display: 'inline-flex',
      color: t.accent,
      '& svg': { fontSize: 15 },
    },
    menuCount: {
      marginLeft: 'auto',
      paddingLeft: 12,
      fontFamily: MONO_FONT,
      fontSize: 11,
      color: t.textMuted,
    },
    search: {
      display: 'flex',
      alignItems: 'center',
      gap: 8,
      height: 34,
      padding: '0 10px',
      borderRadius: 9,
      border: `1px solid ${t.borderStrong}`,
      background: t.inputBg,
      color: t.textMuted,
      transition: 'border-color 0.15s ease',
      '&:focus-within': { borderColor: t.accentBorder },
      '& > svg': { fontSize: 17 },
    },
    searchInput: {
      flex: 1,
      fontSize: 12.5,
      color: t.text,
      '& input::placeholder': { color: t.textMuted, opacity: 1 },
    },
    kbd: {
      fontFamily: MONO_FONT,
      fontSize: 10,
      padding: '1px 5px',
      borderRadius: 4,
      border: `1px solid ${t.borderStrong}`,
      color: t.textMuted,
      cursor: 'pointer',
    },
    chip: {
      display: 'inline-flex',
      alignItems: 'center',
      gap: 5,
      maxWidth: '100%',
      height: 22,
      padding: '0 7px',
      borderRadius: 6,
      fontFamily: MONO_FONT,
      fontSize: 11,
      lineHeight: 1,
      color: t.text,
      background: t.chipBg,
      border: `1px solid ${t.chipBorder}`,
      whiteSpace: 'nowrap',
      textDecoration: 'none',
      '& svg': { fontSize: 13, color: t.textMuted },
    },
    chipText: { overflow: 'hidden', textOverflow: 'ellipsis' },
    chipAccent: {
      color: t.accent,
      background: t.accentBg,
      borderColor: t.accentBorder,
    },
    chipSuccess: { color: t.success, background: t.successBg, borderColor: 'transparent' },
    chipWarning: { color: t.warning, background: t.warningBg, borderColor: 'transparent' },
    chipDanger: { color: t.danger, background: t.dangerBg, borderColor: 'transparent' },
    chipClickable: {
      cursor: 'pointer',
      '&:hover': { borderColor: t.accentBorder },
    },
  };
});

export const useFluxUiStyles = useStyles;

export type Crumb = { label: string; to?: string };

export const FluxPageHeader = (props: {
  crumbs?: Crumb[];
  title: ReactNode;
  count?: ReactNode;
  actions?: ReactNode;
}) => {
  const classes = useStyles();
  const crumbs = props.crumbs ?? [];
  return (
    <header className={classes.header}>
      {crumbs.length > 0 && (
        <nav className={classes.crumbs} aria-label="breadcrumb">
          {crumbs.map((crumb, i) => {
            const last = i === crumbs.length - 1;
            return (
              <span key={`${crumb.label}-${i}`} style={{ display: 'contents' }}>
                {crumb.to && !last ? (
                  <RouterLink to={crumb.to}>{crumb.label}</RouterLink>
                ) : (
                  <span className={last ? classes.crumbCurrent : undefined}>
                    {crumb.label}
                  </span>
                )}
                {!last && <span className={classes.crumbSep}>›</span>}
              </span>
            );
          })}
        </nav>
      )}
      <div className={classes.titleWrap}>
        <h1 className={classes.title}>{props.title}</h1>
        {props.count !== undefined && (
          <span className={classes.countPill}>{props.count}</span>
        )}
      </div>
      {props.actions && <div className={classes.actions}>{props.actions}</div>}
    </header>
  );
};

type FluxButtonProps = Omit<ButtonBaseProps, 'color'> & {
  variant?: 'primary' | 'ghost';
  size?: 'small' | 'medium';
  icon?: ReactNode;
  iconOnly?: boolean;
  active?: boolean;
  to?: string;
  tooltip?: string;
};

export const FluxButton = forwardRef<HTMLButtonElement, FluxButtonProps>(
  (
    {
      variant = 'ghost',
      size = 'medium',
      icon,
      iconOnly,
      active,
      to,
      tooltip,
      className,
      children,
      ...rest
    },
    ref,
  ) => {
    const classes = useStyles();
    const cls = [
      classes.btn,
      variant === 'primary' ? classes.btnPrimary : classes.btnGhost,
      size === 'small' ? classes.btnSmall : '',
      iconOnly ? classes.btnIcon : '',
      active ? classes.btnActive : '',
      className ?? '',
    ].join(' ');
    const linkProps = to ? { component: RouterLink, to } : {};
    const button = (
      <ButtonBase ref={ref} className={cls} {...(linkProps as any)} {...rest}>
        {icon}
        {!iconOnly && children}
      </ButtonBase>
    );
    return tooltip ? (
      <Tooltip title={tooltip}>
        <span style={{ display: 'inline-flex' }}>{button}</span>
      </Tooltip>
    ) : (
      button
    );
  },
);

export type FluxOption = { value: string; label: string; count?: number };

export const FluxSelect = (props: {
  value: string[];
  options: FluxOption[];
  onChange: (value: string[]) => void;
  multiple?: boolean;
  placeholder: string;
  allLabel?: string;
}) => {
  const classes = useStyles();
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const { value, options, multiple } = props;

  const selectedLabels = options
    .filter(o => value.includes(o.value))
    .map(o => o.label);
  let display = props.placeholder;
  if (selectedLabels.length > 2) {
    display = `${selectedLabels.slice(0, 2).join(', ')} +${selectedLabels.length - 2}`;
  } else if (selectedLabels.length > 0) {
    display = selectedLabels.join(', ');
  }

  const toggle = (optionValue: string) => {
    if (!multiple) {
      props.onChange([optionValue]);
      setAnchor(null);
      return;
    }
    props.onChange(
      value.includes(optionValue)
        ? value.filter(v => v !== optionValue)
        : [...value, optionValue],
    );
  };

  return (
    <>
      <ButtonBase
        className={classes.select}
        onClick={e => setAnchor(e.currentTarget)}
      >
        <span className={classes.selectLabel}>{display}</span>
        <ExpandMoreIcon />
      </ButtonBase>
      <Menu
        anchorEl={anchor}
        open={Boolean(anchor)}
        onClose={() => setAnchor(null)}
        getContentAnchorEl={null}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        PaperProps={{
          className: classes.menuPaper,
          style: { width: anchor?.clientWidth },
        }}
      >
        {multiple && props.allLabel && (
          <MenuItem
            className={classes.menuItem}
            onClick={() => {
              props.onChange([]);
              setAnchor(null);
            }}
          >
            <span className={classes.menuCheck}>
              {value.length === 0 && <CheckIcon />}
            </span>
            {props.allLabel}
          </MenuItem>
        )}
        {options.map(option => (
          <MenuItem
            key={option.value}
            className={classes.menuItem}
            selected={!multiple && value.includes(option.value)}
            onClick={() => toggle(option.value)}
          >
            <span className={classes.menuCheck}>
              {value.includes(option.value) && <CheckIcon />}
            </span>
            {option.label}
            {option.count !== undefined && (
              <span className={classes.menuCount}>{option.count}</span>
            )}
          </MenuItem>
        ))}
        {options.length === 0 && (
          <MenuItem className={classes.menuItem} disabled>
            Sin opciones
          </MenuItem>
        )}
      </Menu>
    </>
  );
};

export const FluxSearchInput = forwardRef<
  HTMLInputElement,
  {
    value: string;
    onChange: (value: string) => void;
    placeholder: string;
    className?: string;
  }
>(({ value, onChange, placeholder, className }, ref) => {
  const classes = useStyles();
  return (
    <div className={`${classes.search} ${className ?? ''}`}>
      <SearchIcon />
      <InputBase
        inputRef={ref}
        className={classes.searchInput}
        value={value}
        placeholder={placeholder}
        onChange={e => onChange(e.target.value)}
        onKeyDown={e => {
          if (e.key === 'Escape') onChange('');
        }}
        inputProps={{ 'aria-label': placeholder }}
      />
      <ButtonBase
        className={classes.kbd}
        aria-label="Limpiar búsqueda"
        onClick={() => onChange('')}
      >
        esc
      </ButtonBase>
    </div>
  );
});

export const FluxChip = (props: {
  children: ReactNode;
  icon?: ReactNode;
  tone?: 'default' | 'accent' | 'success' | 'warning' | 'danger';
  to?: string;
  onClick?: () => void;
  title?: string;
}) => {
  const classes = useStyles();
  const tone = {
    default: '',
    accent: classes.chipAccent,
    success: classes.chipSuccess,
    warning: classes.chipWarning,
    danger: classes.chipDanger,
  }[props.tone ?? 'default'];
  const cls = `${classes.chip} ${tone} ${
    props.to || props.onClick ? classes.chipClickable : ''
  }`;
  const content = (
    <>
      {props.icon}
      <span className={classes.chipText}>{props.children}</span>
    </>
  );
  if (props.to) {
    return (
      <RouterLink
        to={props.to}
        className={cls}
        title={props.title}
        onClick={e => e.stopPropagation()}
      >
        {content}
      </RouterLink>
    );
  }
  if (props.onClick) {
    return (
      <ButtonBase className={cls} onClick={props.onClick} title={props.title}>
        {content}
      </ButtonBase>
    );
  }
  return (
    <span className={cls} title={props.title}>
      {content}
    </span>
  );
};
